import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { competitionAPI } from '../api/endpoints';
import { formatXPUNT } from '../utils/helpers';
import { 
  FaArrowLeft, FaUpload, FaTimes, FaPlus, 
  FaTrash, FaTrophy, FaWallet, FaInfoCircle,
  FaCheck, FaImage, FaPercentage
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreateCompetition.css';

const CreateCompetition = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance, fetchBalance } = useWallet();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    visibility: 'public',
    is_free: true,
    join_price: 0,
    joining_code: '',
    total_winners: 3,
    prize_pool: 0, // Prize pool amount in $XPUNT
    prize_distribution: [], // Custom distribution for each winner position
    rules: '',
  });

  // Default prize distribution for different number of winners
  const getDefaultDistribution = (numWinners) => {
    const distributions = {
      1: [100],
      2: [70, 30],
      3: [60, 25, 15],
      4: [50, 25, 15, 10],
      5: [50, 20, 20, 5, 5],
      6: [45, 20, 15, 10, 5, 5],
      7: [40, 20, 15, 10, 8, 4, 3],
      8: [35, 20, 15, 10, 8, 5, 4, 3],
      9: [30, 18, 14, 10, 8, 6, 5, 5, 4],
      10: [25, 17, 13, 10, 8, 7, 6, 5, 5, 4],
    };
    return distributions[numWinners] || Array(numWinners).fill(Math.floor(100 / numWinners));
  };

  // Initialize prize distribution
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      prize_distribution: getDefaultDistribution(prev.total_winners),
    }));
  }, []);

  const isPunterPlus = user?.punter_plus || false;

  useEffect(() => {
    if (!isPunterPlus) {
      toast.error('You need Punter+ to create competitions');
      navigate('/subscription');
    }
  }, [isPunterPlus, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleWinnerCountChange = (count) => {
    const numWinners = parseInt(count) || 1;
    setFormData(prev => ({
      ...prev,
      total_winners: numWinners,
      prize_distribution: getDefaultDistribution(numWinners),
    }));
  };

  const handleDistributionChange = (index, value) => {
    const newDistribution = [...formData.prize_distribution];
    newDistribution[index] = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      prize_distribution: newDistribution,
    }));
  };

  const getTotalPercentage = () => {
    return formData.prize_distribution.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate dates
    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      toast.error('End date must be after start date');
      return;
    }

    // Validate prize pool
    const prizePool = parseFloat(formData.prize_pool) || 0;
    if (prizePool <= 0) {
      toast.error('Please set a prize pool amount');
      return;
    }

    if (prizePool > balance) {
      toast.error(`Insufficient balance. You need ${formatXPUNT(prizePool)} but have ${formatXPUNT(balance)}`);
      return;
    }

    // Validate prize distribution
    const totalPercentage = getTotalPercentage();
    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error(`Prize distribution must total 100%. Currently: ${totalPercentage.toFixed(1)}%`);
      return;
    }

    // Validate prize percentage against prize pool
    setLoading(true);
    try {
      // Prepare distribution data
      const distributionData = formData.prize_distribution.map((percentage, index) => ({
        position: index + 1,
        percentage: parseFloat(percentage),
        amount: (prizePool * parseFloat(percentage)) / 100,
      }));

      const submitData = {
        ...formData,
        prize_pool: prizePool,
        prize_distribution: distributionData,
        total_prize_pool: prizePool,
        image_data: imagePreview,
      };

      const response = await competitionAPI.create(submitData);
      
      if (response.data.success) {
        toast.success('Competition created successfully!');
        await fetchBalance();
        navigate(`/competitions/${response.data.id}`);
      } else {
        toast.error(response.data.message || 'Failed to create competition');
      }
    } catch (error) {
      console.error('Create competition error:', error);
      toast.error(error.response?.data?.message || 'Failed to create competition');
    } finally {
      setLoading(false);
    }
  };

  const totalPercentage = getTotalPercentage();
  const prizePool = parseFloat(formData.prize_pool) || 0;

  return (
    <div className="create-competition-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="create-competition-card">
        <div className="card-header">
          <h1>Create Competition</h1>
          <p className="card-subtitle">Set up a new prediction competition</p>
          {isPunterPlus && (
            <span className="punter-badge">⭐ Punter+</span>
          )}
          <div className="wallet-display">
            <FaWallet /> Balance: {formatXPUNT(balance)}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="create-competition-form">
          {/* Title */}
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              placeholder="e.g., Premier League Predictions"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              placeholder="Describe your competition..."
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>

          {/* Competition Image */}
          <div className="form-group">
            <label>Competition Image</label>
            <div className="image-upload-area">
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Competition preview" />
                  <button 
                    type="button" 
                    className="remove-image" 
                    onClick={removeImage}
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <label className="upload-label">
                  <FaUpload />
                  <span>Click to upload image</span>
                  <span className="upload-hint">PNG, JPG, GIF up to 5MB</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    hidden
                  />
                </label>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="datetime-local"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="datetime-local"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Visibility & Pricing */}
          <div className="form-row">
            <div className="form-group">
              <label>Visibility</label>
              <select
                name="visibility"
                value={formData.visibility}
                onChange={handleChange}
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div className="form-group">
              <label>Joining Code (for private)</label>
              <input
                type="text"
                name="joining_code"
                placeholder="Enter joining code"
                value={formData.joining_code}
                onChange={handleChange}
                disabled={formData.visibility === 'public'}
              />
            </div>
          </div>

          {/* Free Entry */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_free"
                checked={formData.is_free}
                onChange={handleChange}
              />
              <span>Free Entry</span>
            </label>
          </div>

          {!formData.is_free && (
            <div className="form-group">
              <label>Entry Price ($XPUNT)</label>
              <input
                type="number"
                name="join_price"
                placeholder="e.g., 5.00"
                value={formData.join_price}
                onChange={handleChange}
                step="0.01"
                min="0.01"
              />
              <small className="hint">Players will pay this amount to join</small>
            </div>
          )}

          {/* Prize Pool Section */}
          <div className="prize-pool-section">
            <div className="section-title">
              <FaTrophy className="section-icon" /> Prize Pool
            </div>

            <div className="form-group">
              <label>Prize Pool Amount ($XPUNT) *</label>
              <div className="input-with-balance">
                <input
                  type="number"
                  name="prize_pool"
                  placeholder="e.g., 100.00"
                  value={formData.prize_pool}
                  onChange={handleChange}
                  step="0.01"
                  min="0.01"
                  required
                />
                <span className="balance-hint">Balance: {formatXPUNT(balance)}</span>
              </div>
              <small className="hint warning">
                <FaInfoCircle /> This amount will be deducted from your wallet balance
              </small>
              {prizePool > balance && (
                <small className="error-hint">
                  Insufficient balance. You need {formatXPUNT(prizePool - balance)} more.
                </small>
              )}
            </div>

            {/* Winners and Distribution */}
            <div className="form-group">
              <label>Number of Winners</label>
              <input
                type="number"
                name="total_winners"
                value={formData.total_winners}
                onChange={(e) => handleWinnerCountChange(e.target.value)}
                min="1"
                max="20"
              />
              <small className="hint">How many winners will share the prize pool</small>
            </div>

            {/* Distribution Table */}
            <div className="distribution-section">
              <div className="distribution-header">
                <span>Position</span>
                <span>Percentage</span>
                <span>Amount</span>
              </div>
              <div className="distribution-list">
                {formData.prize_distribution.map((percentage, index) => {
                  const amount = (prizePool * parseFloat(percentage || 0)) / 100;
                  return (
                    <div key={index} className="distribution-item">
                      <span className="position">
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        {index > 2 && `${index + 1}th`}
                      </span>
                      <div className="percentage-input">
                        <input
                          type="number"
                          value={percentage}
                          onChange={(e) => handleDistributionChange(index, e.target.value)}
                          min="0"
                          max="100"
                          step="0.1"
                        />
                        <span className="percent-sign">%</span>
                      </div>
                      <span className="amount">
                        {formatXPUNT(amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className={`distribution-total ${Math.abs(totalPercentage - 100) > 0.01 ? 'error' : ''}`}>
                <span>Total:</span>
                <span className="total-percentage">{totalPercentage.toFixed(1)}%</span>
                <span className="total-amount">{formatXPUNT(prizePool)}</span>
              </div>
              {Math.abs(totalPercentage - 100) > 0.01 && (
                <div className="distribution-warning">
                  <FaInfoCircle /> Total percentage must equal 100%. Currently: {totalPercentage.toFixed(1)}%
                </div>
              )}
            </div>
          </div>

          {/* Rules */}
          <div className="form-group">
            <label>Competition Rules</label>
            <textarea
              name="rules"
              placeholder="Explain the rules, scoring system, and how winners are determined..."
              value={formData.rules}
              onChange={handleChange}
              rows={4}
            />
          </div>

          {/* Summary */}
          <div className="summary-section">
            <h3>Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span>Prize Pool</span>
                <span className="highlight">{formatXPUNT(prizePool)}</span>
              </div>
              <div className="summary-item">
                <span>Winners</span>
                <span>{formData.total_winners}</span>
              </div>
              <div className="summary-item">
                <span>Entry Fee</span>
                <span>{formData.is_free ? 'Free' : formatXPUNT(formData.join_price)}</span>
              </div>
              <div className="summary-item">
                <span>Your Balance After</span>
                <span className={balance - prizePool < 0 ? 'negative' : 'positive'}>
                  {formatXPUNT(balance - prizePool)}
                </span>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || prizePool > balance || Math.abs(totalPercentage - 100) > 0.01}
            >
              {loading ? <LoadingSpinner size="small" /> : (
                <>
                  <FaTrophy /> Create Competition ({formatXPUNT(prizePool)})
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCompetition;